import React, { useEffect, useRef, useState } from "react";
import InputMask from "react-input-mask";
import { TextField, Typography, Tab, Switch, Box, Grid, Backdrop, Card } from "@material-ui/core";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import { makeStyles, Theme } from "@material-ui/core/styles";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useAppDispatch, useAppSelector } from "src/app-redux/hooks";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import {
  getRolesAction,
  getTerminalsAction,
} from "src/app-redux/management/actions/managementActions";
import { loading, selectTerminalListing } from "src/app-redux/management/managementSlice";
import { INewUser, IInactiveUserUser } from "src/common/typings/user";
import UseWindowDimensions from "src/common/utils/getWindowDimensions";
import CommonAutoCompleteField from "src/components/CommonAutoCompleteField/CommonAutoCompleteField";
import CommonDrawer from "src/components/CommonDrawer/CommonDrawer";
import CommonTabs from "src/components/CommonTabs/CommonTabs";
import MultiSelect2 from "src/components/molecules/MultiSelect2";
import { Prompt } from "react-router-dom";
import { allTerminalsList } from "src/common/utils/app.dropdowns";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import CommonLoader from "src/components/Loader/CommonLoader";

function a11yProps(index: any) {
  return {
    id: `vertical-tab-${index}`,
    "aria-controls": `vertical-tabpanel-${index}`,
  };
}
const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: "100%",
  },
  heading: {
    fontSize: theme.typography.pxToRem(17),
    flexBasis: "33.33%",
    flexShrink: 0,
    lineHeight: "28px",
  },
  tabPanelHead: {
    display: "flex",
    width: "100%",
    margin: "0px 0 0px",
    justifyContent: "space-between",
    alignItems: "center",
    fontFamily: "Inter",
  },
  formRow: {
    marginBottom: "0px",
  },
  backdrop: {
    zIndex: theme.zIndex.modal + 1,
    color: "#fff",
  },
  tabCardContent: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    overflow: "auto",
  },

  UserMainPanel: {
    width: "100%",
    float: "left",
    padding: "0px 20px",
  },
  MuiOutlinedInput: { paddingTop: "30px", paddingBottom: "30px" },
}));

export interface IForm extends INewUser {}

const validationSchema = Yup.object().shape({
  password: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .max(50, "Maximum 50 characters allowed")
    .required("New Password is required"),
  email: Yup.string()
    .max(50, "Maximum 50 characters allowed")
    .required("This field is required")
    .email("Please enter a valid email"),
  terminalId: Yup.string().required("This field is required"),
  username: Yup.string()
    .max(50, "Maximum 50 characters allowed")
    .required("This field is required"),
  firstName: Yup.string()
    .max(50, "Maximum 50 characters allowed")
    .required("This field is required")
    .matches(/^[-\sa-zA-Z]+$/, "Only alphabets are allowed for this field "),
  lastName: Yup.string()
    .max(50, "Maximum 50 characters allowed")
    .required("This field is required")
    .matches(/^[-\sa-zA-Z]+$/, "Only alphabets are allowed for this field "),
  phoneNumber: Yup.string()
    .max(20, "Maximum 12 characters allowed")
    .min(6, "min 12 characters allowed")
    .test("len", "Invalid Phone Number No.", (val = "") => {
      const val_length_without_dashes = val.replace(/-|_/g, "").length;
      return val_length_without_dashes === 13;
    })
    .required("This field is required"),
  appointment: Yup.string().max(30, "Maximum 30 characters allowed"),
  roles: Yup.array<string[]>().min(1, "At least one role is required."),
});
interface Props {
  allRoles: string[];
  submitCounter: number;
  handleSubmit: (formData: IForm) => Promise<void>;
  initialValues?: IInactiveUserUser;
  userCompStatus?: boolean;
  InactiveList?: any[];
  setusersSelected: (val: string) => void;
  selUser: string;
}

export function AddInactiveUser({
  initialValues,
  handleSubmit,
  submitCounter,
  allRoles,
}: Props): React.ReactElement {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const [value, setValue] = React.useState(0);
  const [showVendorDilog, setShowVendorDilog] = React.useState(false);
  const getAllTerminal = useAppSelector(selectTerminalListing);
  const managementLaoding = useAppSelector(loading);
  const [state, setState] = useState({ showPassword: false });
  const [terminalId, setTerminalId] = React.useState<number>();
  const multiselectRef: any = useRef();

  const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setValue(newValue);
  };

  // from Store
  const formik = useFormik<IForm>({
    enableReinitialize: true,
    initialValues: {
      // these should same values present in Interface
      isADUser: true,
      email: initialValues?.email || "",
      isActive: true,
      username: initialValues?.username || "",
      firstName: initialValues?.firstName || "",
      lastName: initialValues?.lastName || "",
      phoneNumber: initialValues?.phoneNumber ? `${initialValues.phoneNumber}` : "",
      twoFAEnabled: initialValues?.twoFAEnabled || false,
      location: initialValues?.location || "",
      terminalId:
        initialValues?.terminalId || Number(localStorage.getItem("terminalId")) !== 0
          ? Number(localStorage.getItem("terminalId"))
          : "",
      appointment: initialValues?.appointment || "",
      enabledAppNotificiations: initialValues?.enabledAppNotificiations || false,
      enabledSMSNotifications: initialValues?.enabledSMSNotifications || false,
      isAllowToExportData: initialValues?.isAllowToExportData || false,
      password: "",
      roles: [],
    },
    validationSchema: validationSchema,
    onSubmit: (values: any, actions: any) => {
      handleSubmit(values);
    },
  });

  useEffect(() => {
    dispatch(getRolesAction());
    dispatch(getTerminalsAction());
  }, []);

  useEffect(() => {
    formik.setFieldValue("id", 0);
    if (submitCounter) {
      formik.submitForm();
      setIsDirty(false);
    }
  }, [submitCounter]);

  const { height } = UseWindowDimensions();
  const minAccHeight = 194;
  const minTabHeight = 214;
  let accordionMaxHeight = 0;
  let tabMinHeight = 0;
  accordionMaxHeight = height - minAccHeight;
  tabMinHeight = height - minTabHeight;

  const onMultiSelectSelection = (selectedList: any, selectedItem: any) => {
    formik.setFieldValue(
      "roles",
      selectedList.map((item: any) => (item.id ? item.id : ""))
    );
  };
  const onMultiSelectRemove = (selectedList: any, removedItem: any) => {
    formik.setFieldValue(
      "roles",
      selectedList.map((item: any) => (item.id ? item.id : ""))
    );
  };

  // Show prompt if the form is fully or partially filled and user wants to navigate away
  const [isDirty, setIsDirty] = React.useState<boolean>(false);
  useEffect(() => {
    // If prompt, when reloading or closing the tab
    window.onbeforeunload = function (e: Event) {
      if (isDirty) {
        e.preventDefault();
        return "Changes you made may not be saved.";
      }
    };
  }, [isDirty]);
  function handleValueChange(e: any) {
    // Textfield ==>> e.target.value
    // Checkbox / Switch ==>> e.target.checked !== e.target._wrapperState.initialChecked

    formik.handleChange(e);
    e.target.value || JSON.stringify(formik.values) !== JSON.stringify(formik.initialValues)
      ? setIsDirty(true)
      : setIsDirty(false);
  }
  function handleValueChangeTerminal(terId: number) {
    setTerminalId(terId);
    const current = multiselectRef?.current;
    if (current) {
      current?.getUpdated();
    }
    formik.setFieldValue("roles", []);
  }
  const handleClickShowPassword = () => {
    setState({ ...state, showPassword: !state.showPassword });
  };
  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };
  return (
    <>
      {/* If prompt, when navigating to another page inside the app */}
      <Prompt when={isDirty} message="Changes you made may not be saved." />
      <Backdrop className={classes.backdrop} open={managementLaoding}>
        <CommonLoader />
      </Backdrop>
      <Grid container direction="row" alignItems="flex-start">
        <Grid item xs={12} sm={4} md={2}>
          <CommonTabs value={value} handleChange={handleChange} tabMinHeight={tabMinHeight}>
            <Tab label="User information" {...a11yProps(0)} />
          </CommonTabs>
        </Grid>
        <Grid
          item
          xs={12}
          sm={8}
          md={10}
          component={Card}
          className={classes.tabCardContent}
          style={{ height: accordionMaxHeight }}
        >
          <Grid className={classes.UserMainPanel}>
            <form onSubmit={formik.handleSubmit}>
              <Grid className={classes.formRow}>
                <Grid className={classes.tabPanelHead} container>
                  <Grid item xs={12} md={6}>
                    <Typography className={classes.heading}>Personal Details</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(formik.values.isActive)}
                          onChange={formik.handleChange}
                          name="isActive"
                          color="primary"
                        />
                      }
                      label="Active User"
                      style={{ float: "right" }}
                    />
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    mb={2}
                    borderBottom={1}
                    style={{ marginTop: "-3px" }}
                    color={"rgba(0, 0, 0, 0.12)"}
                  />
                </Grid>
              </Grid>
              <Grid container direction="row" alignItems="center" spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box>
                    <TextField
                      required
                      size="small"
                      className="customTextField"
                      fullWidth
                      variant="outlined"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      label="First Name"
                      name="firstName"
                      placeholder="Michael"
                      value={formik.values.firstName}
                      disabled={false}
                      onChange={handleValueChange}
                      onPaste={(e: any) => {
                        if (/[^A-Za-z ]/.test(e.clipboardData.getData("text"))) e.preventDefault();
                      }}
                      error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                      helperText={formik.touched.firstName && formik.errors.firstName}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <TextField
                      required
                      size="small"
                      fullWidth
                      className="customTextField"
                      variant="outlined"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      label="Last Name"
                      name="lastName"
                      placeholder="Jack"
                      disabled={false}
                      value={formik.values.lastName}
                      onChange={handleValueChange}
                      onPaste={(e: any) => {
                        if (/[^A-Za-z ]/.test(e.clipboardData.getData("text"))) e.preventDefault();
                      }}
                      error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                      helperText={formik.touched.lastName && formik.errors.lastName}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <TextField
                      disabled={false}
                      required
                      size="small"
                      fullWidth
                      className="customTextField"
                      variant="outlined"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      placeholder="Enter User Name"
                      label="User Name"
                      name="username"
                      value={formik.values.username}
                      onChange={handleValueChange}
                      error={formik.touched.username && Boolean(formik.errors.username)}
                      helperText={formik.touched.username && formik.errors.username}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <TextField
                      required
                      size="small"
                      fullWidth
                      className="customTextField"
                      variant="outlined"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      label="Email"
                      name="email"
                      placeholder="hello@there.you"
                      type="email"
                      value={formik.values.email}
                      onChange={handleValueChange}
                      error={formik.touched.email && Boolean(formik.errors.email)}
                      helperText={formik.touched.email && formik.errors.email}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <InputMask
                      mask="+99-999-999-9999"
                      value={formik.values.phoneNumber}
                      disabled={false}
                      onChange={handleValueChange}
                    >
                      {() => (
                        <TextField
                          required
                          size="small"
                          fullWidth
                          className="customTextField"
                          variant="outlined"
                          InputLabelProps={{
                            shrink: true,
                          }}
                          label="Phone Number"
                          name="phoneNumber"
                          placeholder="+92-123-456-7890"
                          type="text"
                          value={formik.values.phoneNumber}
                          onChange={handleValueChange}
                          error={formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)}
                          helperText={formik.touched.phoneNumber && formik.errors.phoneNumber}
                        />
                      )}
                    </InputMask>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <TextField
                      required
                      size="small"
                      fullWidth
                      variant="outlined"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      label="Password"
                      className="customTextField"
                      name="password"
                      placeholder="Enter Password"
                      type={state.showPassword ? "text" : "password"}
                      value={formik.values.password}
                      onChange={formik.handleChange}
                      error={formik.touched.password && Boolean(formik.errors.password)}
                      helperText={formik.touched.password && formik.errors.password}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleClickShowPassword}
                              onMouseDown={handleMouseDownPassword}
                              edge="end"
                            >
                              {state.showPassword ? <Visibility /> : <VisibilityOff />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box>
                    <CommonAutoCompleteField
                      cssClassName="customTextField"
                      error={formik.touched.location && Boolean(formik.errors.location)}
                      helperText={formik.touched.location && formik.errors.location}
                      label="Location"
                      placeholder="Select Location"
                      name="location"
                      optionsType={allTerminalsList}
                      getOptionLabel={(option: any) => option.name || ""}
                      value={
                        allTerminalsList.find((st: any) => st.name === formik.values.location) ||
                        null
                      }
                      onChange={(option: any) => {
                        if (option && option.id !== 0) {
                          formik.setFieldValue("location", option.name);
                          handleValueChange({ target: { value: option.name } });
                        }
                      }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <TextField
                      size="small"
                      className="customTextField"
                      fullWidth
                      variant="outlined"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      label="Appointment"
                      name="appointment"
                      placeholder=""
                      value={formik.values.appointment}
                      disabled={false}
                      onChange={handleValueChange}
                      onPaste={(e: any) => {
                        if (/[^A-Za-z ]/.test(e.clipboardData.getData("text"))) e.preventDefault();
                      }}
                      error={formik.touched.appointment && Boolean(formik.errors.appointment)}
                      helperText={formik.touched.appointment && formik.errors.appointment}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <CommonAutoCompleteField
                      required
                      cssClassName="customTextField"
                      error={formik.touched.terminalId && Boolean(formik.errors.terminalId)}
                      helperText={formik.touched.terminalId && formik.errors.terminalId}
                      label="Terminal"
                      placeholder="Select Terminal"
                      name="terminalId"
                      optionsType={getAllTerminal.filter((data: any) => {
                        if (Number(localStorage.getItem("terminalId")) !== 0) {
                          return (
                            data.isActive &&
                            data.terminalId == Number(localStorage.getItem("terminalId"))
                          );
                        } else {
                          return data.isActive && data.terminalId != 0;
                        }
                      })}
                      getOptionLabel={(option: any) => option.terminalName || ""}
                      value={
                        getAllTerminal.find(
                          (st: any) => st.terminalId === formik.values.terminalId
                        ) || null
                      }
                      onChange={(option: any) => {
                        formik.setFieldValue("terminalId", option.terminalId);
                        handleValueChange({ target: { value: option.terminalId } });
                        handleValueChangeTerminal(option.terminalId);
                      }}
                      disabled={Number(localStorage.getItem("terminalId")) != 0}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <MultiSelect2
                    allValues={
                      Number(localStorage.getItem("terminalId")) !== 0
                        ? allRoles
                        : allRoles?.filter((item: any) => item?.terminalId == terminalId)
                    }
                    displayValue={"roleName"}
                    placeholder="User Roles"
                    onSelect={onMultiSelectSelection}
                    onRemove={onMultiSelectRemove}
                    ref={multiselectRef}
                    errorText={formik.touched.roles && formik.errors.roles}
                  />
                </Grid>
                <Grid item xs={12} md={4} lg={3}>
                  <Box>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formik.values.enabledAppNotificiations}
                          onChange={formik.handleChange}
                          name="enabledAppNotificiations"
                          color="primary"
                        />
                      }
                      label="In-App notifications"
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={4} lg={3}>
                  <Box>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formik.values.enabledSMSNotifications}
                          onChange={formik.handleChange}
                          name="enabledSMSNotifications"
                          color="primary"
                        />
                      }
                      label="SMS notifications"
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} md={4} lg={3}>
                  <Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={Boolean(formik.values.isAllowToExportData)}
                          onChange={formik.handleChange}
                          name="isAllowToExportData"
                          color="primary"
                        />
                      }
                      label="Allow to Export Data"
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} md={4} lg={3}>
                  <Box>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formik.values.twoFAEnabled}
                          onChange={formik.handleChange}
                          name="twoFAEnabled"
                          color="primary"
                        />
                      }
                      label="Enable 2FA"
                    />
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Grid>
        </Grid>
      </Grid>
      {showVendorDilog && (
        <CommonDrawer
          heading={"Select Vendor"}
          isOpen={showVendorDilog}
          closeDrawer={() => {
            setShowVendorDilog(false);
          }}
          drawerWidth={950}
          border={true}
        >
          <></>
        </CommonDrawer>
      )}
    </>
  );
}
